// data/items/insights.js
// Ritual Insight items - consumables that grant XP to a selected skill

/**
 * Ritual Insights are XP items that can be used to grant XP
 * to any skill that meets the minimum level requirement.
 *
 * Design rationale:
 * - Tier I: Early boost, ~3 levels from 1
 * - Tier II-III: Mid-game catch-up mechanics
 * - Tier IV-V: Late game progress aids
 * - Tier VI: Endgame prestige reward
 */
export const INSIGHTS = [
    {
        id: 'faint_insight',
        name: 'Faint Insight',
        icon: '💡',
        color: '#8B9DC3',
        description: 'A dim flicker of knowledge. Grants 250 XP to a skill of your choice.',
        value: 500,
        tier: 1,
        xpAmount: 250,
        minLevel: 1,
        usable: true
    },
    {
        id: 'smoldering_insight',
        name: 'Smoldering Insight',
        icon: '🔥',
        color: '#E07B39',
        description: 'A warming ember of wisdom. Grants 1,000 XP to a skill of your choice (requires level 15).',
        value: 2000,
        tier: 2,
        xpAmount: 1000,
        minLevel: 15,
        usable: true
    },
    {
        id: 'kindled_insight',
        name: 'Kindled Insight',
        icon: '🔥',
        color: '#FF6B35',
        description: 'A steady flame of understanding. Grants 3,500 XP to a skill of your choice (requires level 30).',
        value: 7000,
        tier: 3,
        xpAmount: 3500,
        minLevel: 30,
        usable: true
    },
    {
        id: 'blazing_insight',
        name: 'Blazing Insight',
        icon: '🔥',
        color: '#FF4500',
        description: 'A roaring inferno of knowledge. Grants 10,000 XP to a skill of your choice (requires level 50).',
        value: 20000,
        tier: 4,
        xpAmount: 10000,
        minLevel: 50,
        usable: true
    },
    {
        id: 'veil_touched_insight',
        name: 'Veil-Touched Insight',
        icon: '✨',
        color: '#9B59B6',
        description: 'Whispers from beyond the Veil. Grants 35,000 XP to a skill of your choice (requires level 70).',
        value: 70000,
        tier: 5,
        xpAmount: 35000,
        minLevel: 70,
        usable: true
    },
    {
        id: 'convergent_insight',
        name: 'Convergent Insight',
        icon: '🌟',
        color: '#FFD700',
        description: 'Pure crystallized mastery from the Convergence. Grants 100,000 XP to a skill of your choice (requires level 85).',
        value: 200000,
        tier: 6,
        xpAmount: 100000,
        minLevel: 85,
        usable: true
    }
];

/**
 * Locked Insights - Category-restricted XP items
 * These can only be used on skills within their allowed category.
 * Used as quest rewards to give thematic XP bonuses.
 *
 * Categories:
 * - combat: Combat only
 * - gathering: Mining, Woodcutting, Fishing, Gatherer, Farming
 * - production: Smithing, Cooking, Crafting, Woodworking, Glyphbinding, Alchemy
 * - support: Pyre, Architecting, Husbandry, Pilfering, Bounty
 */
export const LOCKED_INSIGHTS = [
    // ============ COMBAT INSIGHTS (Dark Red ⚔️) ============
    {
        id: 'faint_combat_insight',
        name: 'Faint Combat Insight',
        icon: '⚔️',
        color: '#8B0000',
        description: 'A dim flicker of martial knowledge. Grants 250 XP to Combat.',
        value: 500,
        tier: 1,
        xpAmount: 250,
        minLevel: 1,
        usable: true,
        allowedCategories: ['combat']
    },
    {
        id: 'smoldering_combat_insight',
        name: 'Smoldering Combat Insight',
        icon: '⚔️',
        color: '#A52A2A',
        description: 'A warming ember of battle wisdom. Grants 1,000 XP to Combat (requires level 15).',
        value: 2000,
        tier: 2,
        xpAmount: 1000,
        minLevel: 15,
        usable: true,
        allowedCategories: ['combat']
    },
    {
        id: 'kindled_combat_insight',
        name: 'Kindled Combat Insight',
        icon: '⚔️',
        color: '#B22222',
        description: 'A steady flame of combat mastery. Grants 3,500 XP to Combat (requires level 30).',
        value: 7000,
        tier: 3,
        xpAmount: 3500,
        minLevel: 30,
        usable: true,
        allowedCategories: ['combat']
    },
    {
        id: 'blazing_combat_insight',
        name: 'Blazing Combat Insight',
        icon: '⚔️',
        color: '#CD5C5C',
        description: 'A roaring inferno of warrior knowledge. Grants 10,000 XP to Combat (requires level 50).',
        value: 20000,
        tier: 4,
        xpAmount: 10000,
        minLevel: 50,
        usable: true,
        allowedCategories: ['combat']
    },
    {
        id: 'veil_touched_combat_insight',
        name: 'Veil-Touched Combat Insight',
        icon: '⚔️',
        color: '#DC143C',
        description: 'Martial whispers from beyond the Veil. Grants 35,000 XP to Combat (requires level 70).',
        value: 70000,
        tier: 5,
        xpAmount: 35000,
        minLevel: 70,
        usable: true,
        allowedCategories: ['combat']
    },
    {
        id: 'convergent_combat_insight',
        name: 'Convergent Combat Insight',
        icon: '⚔️',
        color: '#FF4444',
        description: 'Pure crystallized battle mastery. Grants 100,000 XP to Combat (requires level 85).',
        value: 200000,
        tier: 6,
        xpAmount: 100000,
        minLevel: 85,
        usable: true,
        allowedCategories: ['combat']
    },

    // ============ GATHERING INSIGHTS (Green ⛏️) ============
    {
        id: 'faint_gathering_insight',
        name: 'Faint Gathering Insight',
        icon: '⛏️',
        color: '#2E8B57',
        description: 'A dim flicker of forager knowledge. Grants 250 XP to a gathering skill.',
        value: 500,
        tier: 1,
        xpAmount: 250,
        minLevel: 1,
        usable: true,
        allowedCategories: ['gathering']
    },
    {
        id: 'smoldering_gathering_insight',
        name: 'Smoldering Gathering Insight',
        icon: '⛏️',
        color: '#3CB371',
        description: 'A warming ember of harvester wisdom. Grants 1,000 XP to a gathering skill (requires level 15).',
        value: 2000,
        tier: 2,
        xpAmount: 1000,
        minLevel: 15,
        usable: true,
        allowedCategories: ['gathering']
    },
    {
        id: 'kindled_gathering_insight',
        name: 'Kindled Gathering Insight',
        icon: '⛏️',
        color: '#228B22',
        description: 'A steady flame of prospector mastery. Grants 3,500 XP to a gathering skill (requires level 30).',
        value: 7000,
        tier: 3,
        xpAmount: 3500,
        minLevel: 30,
        usable: true,
        allowedCategories: ['gathering']
    },
    {
        id: 'blazing_gathering_insight',
        name: 'Blazing Gathering Insight',
        icon: '⛏️',
        color: '#32CD32',
        description: 'A roaring inferno of gatherer knowledge. Grants 10,000 XP to a gathering skill (requires level 50).',
        value: 20000,
        tier: 4,
        xpAmount: 10000,
        minLevel: 50,
        usable: true,
        allowedCategories: ['gathering']
    },
    {
        id: 'veil_touched_gathering_insight',
        name: 'Veil-Touched Gathering Insight',
        icon: '⛏️',
        color: '#00FF7F',
        description: 'Harvester whispers from beyond the Veil. Grants 35,000 XP to a gathering skill (requires level 70).',
        value: 70000,
        tier: 5,
        xpAmount: 35000,
        minLevel: 70,
        usable: true,
        allowedCategories: ['gathering']
    },
    {
        id: 'convergent_gathering_insight',
        name: 'Convergent Gathering Insight',
        icon: '⛏️',
        color: '#7CFC00',
        description: 'Pure crystallized forager mastery. Grants 100,000 XP to a gathering skill (requires level 85).',
        value: 200000,
        tier: 6,
        xpAmount: 100000,
        minLevel: 85,
        usable: true,
        allowedCategories: ['gathering']
    },

    // ============ PRODUCTION INSIGHTS (Orange 🔨) ============
    {
        id: 'faint_production_insight',
        name: 'Faint Production Insight',
        icon: '🔨',
        color: '#CD853F',
        description: 'A dim flicker of crafter knowledge. Grants 250 XP to a production skill.',
        value: 500,
        tier: 1,
        xpAmount: 250,
        minLevel: 1,
        usable: true,
        allowedCategories: ['production']
    },
    {
        id: 'smoldering_production_insight',
        name: 'Smoldering Production Insight',
        icon: '🔨',
        color: '#D2691E',
        description: 'A warming ember of artisan wisdom. Grants 1,000 XP to a production skill (requires level 15).',
        value: 2000,
        tier: 2,
        xpAmount: 1000,
        minLevel: 15,
        usable: true,
        allowedCategories: ['production']
    },
    {
        id: 'kindled_production_insight',
        name: 'Kindled Production Insight',
        icon: '🔨',
        color: '#FF8C00',
        description: 'A steady flame of smith mastery. Grants 3,500 XP to a production skill (requires level 30).',
        value: 7000,
        tier: 3,
        xpAmount: 3500,
        minLevel: 30,
        usable: true,
        allowedCategories: ['production']
    },
    {
        id: 'blazing_production_insight',
        name: 'Blazing Production Insight',
        icon: '🔨',
        color: '#FFA500',
        description: 'A roaring inferno of maker knowledge. Grants 10,000 XP to a production skill (requires level 50).',
        value: 20000,
        tier: 4,
        xpAmount: 10000,
        minLevel: 50,
        usable: true,
        allowedCategories: ['production']
    },
    {
        id: 'veil_touched_production_insight',
        name: 'Veil-Touched Production Insight',
        icon: '🔨',
        color: '#FFB347',
        description: 'Artisan whispers from beyond the Veil. Grants 35,000 XP to a production skill (requires level 70).',
        value: 70000,
        tier: 5,
        xpAmount: 35000,
        minLevel: 70,
        usable: true,
        allowedCategories: ['production']
    },
    {
        id: 'convergent_production_insight',
        name: 'Convergent Production Insight',
        icon: '🔨',
        color: '#FFCC00',
        description: 'Pure crystallized crafter mastery. Grants 100,000 XP to a production skill (requires level 85).',
        value: 200000,
        tier: 6,
        xpAmount: 100000,
        minLevel: 85,
        usable: true,
        allowedCategories: ['production']
    },

    // ============ SUPPORT INSIGHTS (Purple ✨) ============
    {
        id: 'faint_support_insight',
        name: 'Faint Support Insight',
        icon: '✨',
        color: '#6A5ACD',
        description: 'A dim flicker of auxiliary knowledge. Grants 250 XP to a support skill.',
        value: 500,
        tier: 1,
        xpAmount: 250,
        minLevel: 1,
        usable: true,
        allowedCategories: ['support']
    },
    {
        id: 'smoldering_support_insight',
        name: 'Smoldering Support Insight',
        icon: '✨',
        color: '#7B68EE',
        description: 'A warming ember of arcane wisdom. Grants 1,000 XP to a support skill (requires level 15).',
        value: 2000,
        tier: 2,
        xpAmount: 1000,
        minLevel: 15,
        usable: true,
        allowedCategories: ['support']
    },
    {
        id: 'kindled_support_insight',
        name: 'Kindled Support Insight',
        icon: '✨',
        color: '#8A2BE2',
        description: 'A steady flame of utility mastery. Grants 3,500 XP to a support skill (requires level 30).',
        value: 7000,
        tier: 3,
        xpAmount: 3500,
        minLevel: 30,
        usable: true,
        allowedCategories: ['support']
    },
    {
        id: 'blazing_support_insight',
        name: 'Blazing Support Insight',
        icon: '✨',
        color: '#9370DB',
        description: 'A roaring inferno of support knowledge. Grants 10,000 XP to a support skill (requires level 50).',
        value: 20000,
        tier: 4,
        xpAmount: 10000,
        minLevel: 50,
        usable: true,
        allowedCategories: ['support']
    },
    {
        id: 'veil_touched_support_insight',
        name: 'Veil-Touched Support Insight',
        icon: '✨',
        color: '#BA55D3',
        description: 'Mystical whispers from beyond the Veil. Grants 35,000 XP to a support skill (requires level 70).',
        value: 70000,
        tier: 5,
        xpAmount: 35000,
        minLevel: 70,
        usable: true,
        allowedCategories: ['support']
    },
    {
        id: 'convergent_support_insight',
        name: 'Convergent Support Insight',
        icon: '✨',
        color: '#DA70D6',
        description: 'Pure crystallized auxiliary mastery. Grants 100,000 XP to a support skill (requires level 85).',
        value: 200000,
        tier: 6,
        xpAmount: 100000,
        minLevel: 85,
        usable: true,
        allowedCategories: ['support']
    }
];

export default INSIGHTS;
